import React, { useEffect, useMemo, useState } from 'react'
import metadata from './data/fonts.json'
import { PAIRING_CATEGORIES } from './data/pairings'

type FontMeta = {
  family: string
  category: string
  subsets: string[]
  variants: string[]
}

type FontRecord = Record<string, FontMeta>

const ALL_FONTS = Object.values(metadata as FontRecord)

const DEFAULT_SAMPLE =
  'The quick brown fox jumps over the lazy dog. 0123456789 — Design for humans.'

const buildFontsUrl = (families: string[]) => {
  if (families.length === 0) return null
  const params = families
    .map((family) => `family=${family.replace(/ /g, '+')}:wght@400;600`)
    .join('&')
  return `https://fonts.googleapis.com/css2?${params}&display=swap`
}

const CATEGORIES = ['sans-serif', 'serif', 'display', 'handwriting', 'monospace']
const ROUTES = ['explorer', 'pairings'] as const
type Route = (typeof ROUTES)[number]

export default function App() {
  const [route, setRoute] = useState<Route>(() => {
    if (typeof window === 'undefined') return 'explorer'
    return window.location.hash.replace('#/', '') === 'pairings' ? 'pairings' : 'explorer'
  })
  const [query, setQuery] = useState('')
  const [categories, setCategories] = useState<string[]>([])
  const [sample, setSample] = useState(DEFAULT_SAMPLE)
  const [fontSize, setFontSize] = useState(24)
  const [visibleCount, setVisibleCount] = useState(100)

  const filteredFonts = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return ALL_FONTS.filter((font) => {
      const matchesCategory =
        categories.length === 0 || categories.includes(font.category)
      const matchesQuery =
        normalized.length === 0 || font.family.toLowerCase().includes(normalized)
      return matchesCategory && matchesQuery
    })
  }, [query, categories])

  const visibleFonts = useMemo(
    () => filteredFonts.slice(0, visibleCount),
    [filteredFonts, visibleCount]
  )

  const pairingFonts = useMemo(() => {
    const families = new Set<string>()
    PAIRING_CATEGORIES.forEach((category) => {
      category.pairings.forEach((pairing) => {
        families.add(pairing.display)
        families.add(pairing.body)
      })
    })
    return Array.from(families)
  }, [])

  useEffect(() => {
    const families = route === 'pairings' ? pairingFonts : visibleFonts.map((font) => font.family)
    const url = buildFontsUrl(families)
    if (!url || typeof document === 'undefined') return

    const existing = document.getElementById('google-font-explorer-fonts') as HTMLLinkElement | null
    if (existing) {
      existing.href = url
      return
    }

    const link = document.createElement('link')
    link.id = 'google-font-explorer-fonts'
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)
  }, [route, pairingFonts, visibleFonts])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!window.location.hash) {
      window.location.hash = '#/explorer'
    }
    const onHashChange = () => {
      setRoute(window.location.hash.replace('#/', '') === 'pairings' ? 'pairings' : 'explorer')
    }
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  return (
    <div className="min-h-screen bg-[#f7f6f3] text-gray-900">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
                Nimble Labs
              </p>
              <h1 className="text-2xl font-semibold">Google Font Explorer</h1>
              <p className="text-sm text-gray-500">
                Scan hundreds of fonts quickly. Type to filter, then scroll.
              </p>
            </div>
            <a
              href="/"
              className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
            >
              Back to Nimble Labs
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                setRoute('explorer')
                window.location.hash = '#/explorer'
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border cursor-pointer ${
                route === 'explorer'
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 text-gray-600'
              }`}
            >
              Explorer
            </button>
            <button
              onClick={() => {
                setRoute('pairings')
                window.location.hash = '#/pairings'
              }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full border cursor-pointer ${
                route === 'pairings'
                  ? 'border-gray-900 bg-gray-900 text-white'
                  : 'border-gray-200 text-gray-600'
              }`}
            >
              Pairings
            </button>
          </div>

          {route === 'explorer' && (
            <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600">Sample text</label>
              <input
                value={sample}
                onChange={(event) => setSample(event.target.value)}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600">Filter by name</label>
              <input
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value)
                  setVisibleCount(100)
                }}
                placeholder="e.g. Inter"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-600">Categories</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((item) => {
                  const isChecked = categories.includes(item)
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs cursor-pointer ${
                        isChecked
                          ? 'border-gray-900 bg-gray-900 text-white'
                          : 'border-gray-200 text-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(event) => {
                          const next = event.target.checked
                            ? [...categories, item]
                            : categories.filter((value) => value !== item)
                          setCategories(next)
                          setVisibleCount(100)
                        }}
                        className="hidden"
                      />
                      {item.replace('-', ' ')}
                    </label>
                  )
                })}
              </div>
              <button
                onClick={() => {
                  setCategories([])
                  setVisibleCount(100)
                }}
                className="text-xs text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                Clear categories
              </button>
            </div>
          </div>
          )}

          <div className="flex flex-wrap items-center gap-6 text-xs text-gray-500">
            {route === 'explorer' && (
              <div>
                {filteredFonts.length} fonts · showing {visibleFonts.length}
              </div>
            )}
            <label className="flex items-center gap-2">
              <span>Size</span>
              <input
                type="range"
                min={16}
                max={64}
                step={2}
                value={fontSize}
                onChange={(event) => setFontSize(Number(event.target.value))}
                className="cursor-pointer"
              />
              <span>{fontSize}px</span>
            </label>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {route === 'explorer' && (
          <div className="grid gap-4 grid-cols-1">
            {visibleFonts.map((font) => (
              <div
                key={font.family}
                className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="uppercase tracking-[0.12em]">{font.category}</span>
                  <span>{font.subsets.join(', ')}</span>
                </div>
                <div className="mt-2 text-lg font-semibold">{font.family}</div>
                <p
                  className="mt-3 text-gray-800"
                  style={{
                    fontFamily: `'${font.family}', system-ui, sans-serif`,
                    fontSize: `${fontSize}px`,
                  }}
                >
                  {sample}
                </p>
              </div>
            ))}
          </div>
        )}

        {route === 'pairings' && (
          <div className="space-y-8">
            {PAIRING_CATEGORIES.map((category) => (
              <section key={category.id} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold">{category.name}</h2>
                    <p className="text-sm text-gray-500">{category.description}</p>
                  </div>
                  <span className="text-xs text-gray-400">
                    {category.pairings.length} pairings
                  </span>
                </div>
                <div className="grid gap-4">
                  {category.pairings.map((pairing) => (
                    <div
                      key={`${category.id}-${pairing.name}`}
                      className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
                        <span className="uppercase tracking-[0.12em]">{pairing.name}</span>
                        <span>
                          Display: {pairing.display} · Body: {pairing.body}
                        </span>
                      </div>
                      <h3
                        className="mt-3 text-2xl"
                        style={{
                          fontFamily: `'${pairing.display}', system-ui, sans-serif`,
                          fontSize: `${fontSize + 6}px`,
                        }}
                      >
                        {pairing.name}
                      </h3>
                      <p className="mt-2 text-sm text-gray-500">{pairing.description}</p>
                      <p
                        className="mt-4 text-gray-800"
                        style={{
                          fontFamily: `'${pairing.body}', system-ui, sans-serif`,
                          fontSize: `${fontSize}px`,
                        }}
                      >
                        {sample}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {route === 'explorer' && visibleFonts.length < filteredFonts.length && (
          <div className="flex justify-center">
            <button
              onClick={() => setVisibleCount((prev) => prev + 100)}
              className="px-4 py-2 text-sm font-semibold text-gray-900 border border-gray-200 rounded-md hover:border-gray-400 cursor-pointer"
            >
              Load more fonts
            </button>
          </div>
        )}
      </main>
    </div>
  )
}
